<template>
  <div class="date-time-range-input clearfix">
    <!-- From Date -->
    <div class="form-group">
      <label class="col-sm-4 control-label">
        {{ field.startLabel }}
        <span v-if="field.required" class="required">*</span>
      </label>
      <div class="col-sm-8">
        <div class="row">
          <div class="col-md-12 col-lg-6 margin-bottom">
            <div class="input-group">
              <span class="input-group-addon">
                <span class="fa fa-calendar"></span>
              </span>
              <input v-el:start class="form-control" type="text"/>
            </div>
          </div>
          <div class="col-md-12 col-lg-6">
            <time-input v-ref:start-time></time-input>
          </div>
        </div>
        <!-- Validation messages -->
        <div class="row">
          <div class="col-sm-12">
            <span class="form-validation-error" v-show="!validStart">
              <span class="fa fa times"></span>
              {{ field.startLabel }} date is not a valid date.
            </span>
          </div>
        </div>
      </div>
    </div>
    <!-- To Date -->
    <div class="form-group">
      <label class="col-sm-4 control-label">
        {{ field.endLabel }}
        <span v-if="field.required" class="required">*</span>
      </label>
      <div class="col-sm-8">
        <div class="row">
          <div class="col-md-12 col-lg-6 margin-bottom">
            <div class="input-group">
              <span class="input-group-addon">
                <span class="fa fa-calendar"></span>
              </span>
              <input v-el:end class="form-control" type="text"/>
            </div>
          </div>
          <div class="col-md-12 col-lg-6">
            <time-input v-ref:end-time></time-input>
          </div>
        </div>
        <!-- Validation messages -->
        <div class="row">
          <div class="col-sm-12">
            <span class="form-validation-error" v-show="!validEnd">
              <span class="fa fa times"></span>
              {{ field.endLabel }} date is not a valid date.
            </span>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-12">
            <span class="form-validation-error" v-show="!valid">
              <span class="fa fa times"></span>
              The range you selected is invalid.
            </span>
            <span class="form-validation-error" v-show="!validDuration">
              <span class="fa fa times"></span>
              A booking's duration can be only be from {{ field.minimumMinutes }}
              minutes up to {{ field.maximumDays }} days
            </span>
          </div>
        </div>
      </div>
    </div>
    <input type="hidden" :name="name + '[start]'" v-model="field.startDate"/>
    <input type="hidden" :name="name + '[end]'" v-model="field.endDate"/>
  </div>
</template>

<script>
import TimeInput from './TimeInput'

export default {
  name: 'DateTimeRangeInput',
  components: {
    TimeInput
  },
  props: {
    field: {
      type: Object,
      default () { return {} }
    }
  },
  computed: {
    valid () {
      return this.validStart && this.validEnd && this.validRange &&
      this.validEndTime && this.validStartTime && this.validDuration
    },
    validRange () {
      if (this.field.startDate && this.field.endDate) {
        return moment(this.field.endDate).isAfter(this.field.startDate)
      } else {
        return false
      }
    },
    validDuration () {
      let m = moment(this.field.endDate)
      .diff(this.field.startDate, 'minutes', true)
      let d = moment(this.field.endDate)
      .diff(this.field.startDate, 'days', true)
      return m >= this.field.minimumMinutes && d <= this.field.maximumDays
    }
  },
  data () {
    return {
      startPicker: {},
      endPicker: {},
      validStart: false,
      validEnd: false,
      validEndTime: false,
      validStartTime: false
    }
  },
  ready () {
    const $vm = this
    let now = new Date()
    // default options that merge with prop values
    const defOpts = {
      name: 'dateRange',
      startLabel: 'Start Date',
      endLabel: 'End Date',
      startDate: now,
      endDate: null,
      format: 'DD/MM/YYYY',
      minDate: now,
      maxDate: moment().add(5, 'days').toDate(),
      minimumMinutes: 60,
      maximumDays: 5,
      required: true
    }
    let field = Object.assign(defOpts, this.field)
    this.field = field
    this.field.maxDate = moment(this.field.minDate).add(3, 'months').toDate()
    // initialize date pickers
    this.startPicker = new Pikaday({
      field: this.$els.start,
      format: this.field.format,
      defaultDate: this.field.startDate,
      setDefaultDate: true,
      minDate: this.field.minDate,
      maxDate: this.field.maxDate,
      startRange: this.field.startDate,
      endRange: this.field.endDate,
      onSelect (date) {
        $vm.startPicker.setStartRange(date)
        if (moment($vm.field.endDate).isBefore(date)) {
          $vm.endPicker.setDate(date)
        }
        $vm.endPicker.setStartRange(date)
        $vm.endPicker.setMinDate(date)
        $vm.endPicker
        $vm.$refs.startTime.$emit('dateChanged', date)
        let time = $vm.$refs.startTime.getTime()
        let valid = $vm.validateDate(date, time)
        if (valid) {
          $vm.validStart = true
          $vm.field.startDate = valid
        }
      }
    })
    this.endPicker = new Pikaday({
      field: this.$els.end,
      format: this.field.format,
      defaultDate: this.field.endDate,
      setDefaultDate: true,
      minDate: this.field.minDate,
      maxDate: this.field.maxDate,
      startRange: this.field.startDate,
      endRange: this.field.endDate,
      onSelect (date) {
        $vm.startPicker.setEndRange(date)
        $vm.endPicker.setEndRange(date)
        $vm.$refs.endTime.$emit('dateChanged', date)
        let time = $vm.$refs.endTime.getTime()
        let valid = $vm.validateDate(date, time)
        if (valid) {
          $vm.validEnd = true
          $vm.field.endDate = valid
        }
      }
    })
    // fix initial starting dates
    let se = this.field.startDate
    let ee = this.field.endDate
    if (se) {
      this.$refs.startTime.setTime(se)
      this.startPicker.setStartRange(se)
      this.endPicker.setStartRange(se)
      this.endPicker.setMinDate(se)
      this.$refs.startTime.$emit('dateChanged', se)
    }
    if (ee) {
      this.$refs.endTime.setTime(ee)
      this.startPicker.setEndRange(ee)
      this.endPicker.setEndRange(ee)
      this.$refs.endTime.$emit('dateChanged', ee)
    }
    // perform initial validation
    let startDate = this.startPicker.getDate()
    let startTime = this.$refs.startTime.getTime()
    let validStart = this.validateDate(startDate, startTime)
    if (validStart) {
      this.validStart = true
      this.validStartTime = true
      this.field.startDate = validStart
    }
    let endDate = this.endPicker.getDate()
    let endTime = this.$refs.endTime.getTime()
    let validEnd = this.validateDate(endDate, endTime)
    if (validEnd) {
      this.validEnd = true
      this.validEndTime = true
      this.field.endDate = validEnd
    }
    // attach listeners to time pickers
    this.$refs.endTime.$on('timeChanged', function (time) {
      let date = $vm.endPicker.getDate()
      let valid = $vm.validateDate(date, time)
      if (valid) {
        $vm.validEnd = true
        $vm.validEndTime = true
        $vm.field.endDate = valid
      }
    })
    this.$refs.startTime.$on('timeChanged', function (time) {
      let date = $vm.startPicker.getDate()
      let valid = $vm.validateDate(date, time)
      if (valid) {
        $vm.validStart = true
        $vm.validStartTime = true
        $vm.field.startDate = valid
      }
    })
  },
  methods: {
    validateDate (date, time) {
      if (!date || !time) return false
      date = moment(date)
      time = moment(time)
      let dateObj = {
        year: 0,
        month: 0,
        date: 0,
        hour: 0,
        minute: 0
      }
      dateObj.year = date.get('year')
      dateObj.month = date.get('month')
      dateObj.date = date.get('date')
      dateObj.hour = time.get('hour')
      dateObj.minute = time.get('minute')
      let check = moment(dateObj)
      this.$nextTick(function () {
        this.$dispatch('dateTimeChanged')
      })
      if (check.isValid()) {
        return check.toDate()
      } else {
        return false
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// DateTimeRangeInput.vue?cf782558